import { useCallback, useMemo } from "react";
import { PolicyConditionKey } from "features/RolesAndPermissions/constants/policy.constants";
import { useProfile } from "./useProfile";

export const useAccess = () => {
  const { isSuperAdmin, permissions, user } = useProfile();

  const permissionMap = useMemo(() => {
    const map = new Map<string, Set<string>>();

    for (const p of permissions || []) {
      map.set(p.code, new Set(p.policies || []));
    }

    return map;
  }, [permissions]);

  const can = useCallback(
    (permission: string, context?: { ownerId?: number }) => {
      if (isSuperAdmin) return true;

      // 👇 OWN check (frontend shortcut)
      if (context?.ownerId && context.ownerId === user?.id) {
        return true;
      }

      return permissionMap.has(permission);
    },
    [isSuperAdmin, permissionMap, user?.id],
  );

  const canWithPolicy = useCallback(
    (permission: string, policy?: string, context?: { ownerId?: number }) => {
      if (isSuperAdmin) return true;

      const policies = permissionMap.get(permission);

      if (!policies) return false;

      // If no policy required → just permission check
      if (!policy) return true;

      // Policy match
      if (policies.has(policy)) {
        return true;
      }

      // Optional: frontend OWN shortcut
      if (
        policy === PolicyConditionKey.OWNED_BY_SELF &&
        context?.ownerId === user?.id
      ) {
        return true;
      }

      return false;
    },
    [isSuperAdmin, permissionMap, user?.id],
  );

  return {
    can,
    canWithPolicy,
  };
};
